import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { useHandleSubmitWrongPolicyNoCDV } from 'forms/hooks';
import { includes } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import useValidateFieldsPromise from '@gef-ui/form-core/hooks/form/useValidateFieldsPromise';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider, Dialog } from 'components';
import AutocompleteCustom from 'components/atoms/GefMui/AutocompleteCustom';
import SectionHeader from 'components/atoms/GefMui/SectionHeader';
import TextFieldWithSearchIcon from 'components/atoms/GefMui/TextFieldWithSearchIcon';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import MODALS from 'constants/modals';
import { searchPS } from 'features/views/taskCreatingPages/actions';
import { dialogs } from 'features/views/taskCreatingPages/constants';
import {
	BACKOFFICE_FORM_NAME,
	FIELDS,
	MOBILE_SCAN_FORM_NAME,
	SELLER_REQUEST_FORM_NAME,
} from 'features/views/taskCreatingPages/form';
import { getIsPolicyFieldsEditable } from 'features/views/taskCreatingPages/selectors';

import PolicySearchModal from '../modals/policySearch/PolicySearchModal';

export const InformationPS = ({ formName, backOfficeSegmentCodelist }) => {
	const dispatch = useDispatch();
	const fieldsEditable = useSelector(getIsPolicyFieldsEditable);
	const validateFields = useValidateFieldsPromise();

	const handleSubmit = useCallback(() => {
		fieldsEditable && dispatch(searchPS(formName));
	}, [dispatch, fieldsEditable, formName]);

	const handleSubmitWrongPolicyNoCDV = useHandleSubmitWrongPolicyNoCDV({ continueFn: handleSubmit });

	const handlePSSearch = useCallback(async () => {
		const fieldsToValidate = includes(formName, [SELLER_REQUEST_FORM_NAME, MOBILE_SCAN_FORM_NAME])
			? [[FIELDS.policyNumber], [FIELDS.proposalNumber]]
			: [[FIELDS.policyNumber]];

		try {
			await validateFields(fieldsToValidate);
			handleSubmit();
		} catch (error) {
			handleSubmitWrongPolicyNoCDV(null, error);
		}
	}, [handleSubmit, validateFields, handleSubmitWrongPolicyNoCDV, formName]);

	return (
		<Paper sx={{ p: 3, borderRadius: 2 }}>
			<Dialog name={dialogs.NO_PS_NUMBER} mui>
				{getLabelMessage('taskCreatingPages:noPSNumber')}
			</Dialog>
			<PolicySearchModal id={MODALS.policy_search} />
			<Stack spacing={2}>
				<SectionHeader text={getLabelMessage('taskCreatingPages:psInfo')} />
				<Grid container>
					<Grid size={12}>
						<Grid container spacing={2}>
							<Tooltip title={getLabelMessage('tooltips:policyNo')}>
								<Grid size={4}>
									<TextFieldWithSearchIcon
										onSearch={handlePSSearch}
										fieldName={FIELDS.policyNumber}
										maxLength={MaxLengths.POLICY_NO}
										label={getLabelMessage('taskCreatingPages:policyNumber')}
										textFieldDisabled={!fieldsEditable}
										searchIconDisabled={!fieldsEditable}
									/>
								</Grid>
							</Tooltip>
							{includes(formName, [SELLER_REQUEST_FORM_NAME, MOBILE_SCAN_FORM_NAME]) && (
								<Tooltip title={getLabelMessage('tooltips:proposalNo')}>
									<Grid size={4}>
										<TextFieldWithSearchIcon
											onSearch={handlePSSearch}
											fieldName={FIELDS.proposalNumber}
											maxLength={MaxLengths.PROPOSAL_NO}
											label={getLabelMessage('taskCreatingPages:proposalNumber')}
											textFieldDisabled={!fieldsEditable}
											searchIconDisabled={!fieldsEditable}
										/>
									</Grid>
								</Tooltip>
							)}
							<Grid size={4}>
								<TextField
									size="small"
									fieldName={FIELDS.policyHolderFirstName}
									label={getLabelMessage('taskCreatingPages:policyHolderFirstName')}
									disabled
								/>
							</Grid>
							<Grid size={4}>
								<TextField
									size="small"
									fieldName={FIELDS.policyHolderSurname}
									label={getLabelMessage('taskCreatingPages:policyHolderSurname')}
									disabled
								/>
							</Grid>
							<Grid size={4}>
								<TextField
									size="small"
									fieldName={FIELDS.policyHolderBusinessName}
									label={getLabelMessage('taskCreatingPages:policyHolderBusinessName')}
									disabled
								/>
							</Grid>
							{includes(formName, [BACKOFFICE_FORM_NAME, MOBILE_SCAN_FORM_NAME]) && (
								<Tooltip title={getLabelMessage('tooltips:claimNoCP')}>
									<Grid size={4}>
										<TextField
											size="small"
											fieldName={FIELDS.claimNoCP}
											label={getLabelMessage('taskCreatingPages:claimNoCP')}
											slotProps={{
												htmlInput: {
													maxLength: MaxLengths.CLAIM_NO,
												},
											}}
										/>
									</Grid>
								</Tooltip>
							)}
							{formName === BACKOFFICE_FORM_NAME && (
								<Grid size={4}>
									<AutocompleteCustom
										size="small"
										fieldName={FIELDS.backOfficeSegment}
										label={getLabelMessage('taskCreatingPages:backOfficeSegment')}
										options={backOfficeSegmentCodelist}
										labelPropName="shortDescription"
										required
									/>
								</Grid>
							)}
							{formName === SELLER_REQUEST_FORM_NAME && (
								<Grid size={4}>
									<TextField
										size="small"
										fieldName={FIELDS.policyStatus}
										label={getLabelMessage('taskCreatingPages:policyStatus')}
										disabled
									/>
								</Grid>
							)}
						</Grid>
					</Grid>
				</Grid>
			</Stack>
		</Paper>
	);
};

InformationPS.propTypes = {
	backOfficeSegmentCodelist: PropTypes.array,
	formName: PropTypes.string,
};

export default codelistProvider(codelists.backOfficeSegment, 'backOfficeSegmentCodelist')(InformationPS);
